import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';
import { ApiResponse, PaginatedResponse, PaginationParams } from '../models/api-response.model';
import { SpinnerService } from './spinner.service';
import { NotificationService } from 'src/app/shared/isolutions-ui/feedback/notifications/notification.service';

const JSON_HEADERS = new HttpHeaders({ 'Content-Type': 'application/json' });

/**
 * BaseApiService — serviço base para todas as chamadas HTTP.
 *
 * Responsabilidades:
 * - Monta URL base automaticamente
 * - Gerencia spinner (show/hide) por requisição
 * - Exibe mensagens do backend via toastr (nunca mockadas)
 * - Suporta paginação e query params
 *
 * Uso: estenda este serviço nos services de feature.
 *
 * @example
 * export class UserApiService extends BaseApiService {
 *   constructor(http: HttpClient, notify: NotificationService, translate: TranslateService, spinner: SpinnerService) {
 *     super(http, notify, translate, spinner);
 *   }
 *   getUsers(params: PaginationParams) {
 *     return this.getPaginated<User>('User', params);
 *   }
 * }
 */
@Injectable({ providedIn: 'root' })
export class BaseApiService {
  protected readonly baseUrl = environment.apiBaseAddress;

  constructor(
    protected http: HttpClient,
    protected notify: NotificationService,
    protected translate: TranslateService,
    protected spinnerService: SpinnerService,
  ) {}

  // ─── GET ────────────────────────────────────────────────────────────────────

  protected get<T>(endpoint: string, params?: HttpParams, showSpinner = true): Observable<ApiResponse<T>> {
    if (showSpinner) this.spinnerService.show();

    return this.http
      .get<ApiResponse<T>>(`${this.baseUrl}${endpoint}`, { headers: JSON_HEADERS, params })
      .pipe(
        tap((res) => this.handleResponse(res)),
        finalize(() => { if (showSpinner) this.spinnerService.hide(); }),
      );
  }

  protected getPaginated<T>(
    endpoint: string,
    pagination: PaginationParams,
    extraParams?: HttpParams,
    showSpinner = true,
  ): Observable<PaginatedResponse<T>> {
    if (showSpinner) this.spinnerService.show();

    let params = extraParams ?? new HttpParams();
    params = params
      .set('page', pagination.page.toString())
      .set('pageSize', pagination.pageSize.toString());

    if (pagination.sortBy) {
      params = params.set('sortBy', pagination.sortBy);
      params = params.set('sortDirection', pagination.sortDirection ?? 'asc');
    }

    return this.http
      .get<PaginatedResponse<T>>(`${this.baseUrl}${endpoint}`, { headers: JSON_HEADERS, params })
      .pipe(
        tap((res) => this.handleResponse(res)),
        finalize(() => { if (showSpinner) this.spinnerService.hide(); }),
      );
  }

  // ─── POST ───────────────────────────────────────────────────────────────────

  protected post<T>(endpoint: string, body: unknown, showSpinner = true): Observable<ApiResponse<T>> {
    if (showSpinner) this.spinnerService.show();

    return this.http
      .post<ApiResponse<T>>(`${this.baseUrl}${endpoint}`, body, { headers: JSON_HEADERS })
      .pipe(
        tap((res) => this.handleResponse(res)),
        finalize(() => { if (showSpinner) this.spinnerService.hide(); }),
      );
  }

  // ─── PUT ────────────────────────────────────────────────────────────────────

  protected put<T>(endpoint: string, body: unknown, showSpinner = true): Observable<ApiResponse<T>> {
    if (showSpinner) this.spinnerService.show();

    return this.http
      .put<ApiResponse<T>>(`${this.baseUrl}${endpoint}`, body, { headers: JSON_HEADERS })
      .pipe(
        tap((res) => this.handleResponse(res)),
        finalize(() => { if (showSpinner) this.spinnerService.hide(); }),
      );
  }

  // ─── PATCH ──────────────────────────────────────────────────────────────────

  protected patch<T>(endpoint: string, body: unknown, showSpinner = true): Observable<ApiResponse<T>> {
    if (showSpinner) this.spinnerService.show();

    return this.http
      .patch<ApiResponse<T>>(`${this.baseUrl}${endpoint}`, body, { headers: JSON_HEADERS })
      .pipe(
        tap((res) => this.handleResponse(res)),
        finalize(() => { if (showSpinner) this.spinnerService.hide(); }),
      );
  }

  // ─── DELETE ─────────────────────────────────────────────────────────────────

  protected delete<T>(endpoint: string, showSpinner = true): Observable<ApiResponse<T>> {
    if (showSpinner) this.spinnerService.show();

    return this.http
      .delete<ApiResponse<T>>(`${this.baseUrl}${endpoint}`, { headers: JSON_HEADERS })
      .pipe(
        tap((res) => this.handleResponse(res)),
        finalize(() => { if (showSpinner) this.spinnerService.hide(); }),
      );
  }

  // ─── Response Handler ───────────────────────────────────────────────────────

  /**
   * Trata a resposta da API:
   * - Se NÃO for sucesso, exibe messageDescription do backend via toastr.error
   * - Se tiver messageDescription mesmo com sucesso, exibe como warning
   * - NUNCA exibe mensagem mockada — sempre vem do backend
   */
  private handleResponse(res: ApiResponse | PaginatedResponse): void {
    if (!res) return;

    if (!res.isSuccessful && res.messageDescription) {
      this.notify.error(res.messageDescription);
    }
  }
}

